import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, CheckCircle } from 'lucide-react';
import MainLayout from '../../components/layout/MainLayout';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import { useToast } from '../../context/ToastContext';
import { autoHyphenPhone, todayISO } from '../../components/obituary/obituaryUtils';

const SUPPORT_PHONE = '1588-0000';

/**
 * ObituaryRequestPage - 부고등록신청 페이지
 * 부고 작성이 어려운 사용자를 위한 신청 폼
 */
const ObituaryRequestPage = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [formData, setFormData] = useState({
    requesterName: '',
    requesterPhone: '',
    requesterEmail: '',
    deceasedName: '',
    funeralHome: '',
    funeralAddress: '',
    departureDate: '',
    message: '',
  });

  const [errors, setErrors] = useState({});
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [requestNumber, setRequestNumber] = useState(null);

  // 폼 데이터 변경
  const handleChange = (e) => {
    const { name, value } = e.target;
    const nextValue = name === 'requesterPhone' ? autoHyphenPhone(value) : value;
    setFormData(prev => ({ ...prev, [name]: nextValue }));

    // 에러 초기화
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // 유효성 검사
  const validate = () => {
    const newErrors = {};

    if (!formData.requesterName.trim()) {
      newErrors.requesterName = '신청자 이름을 입력해주세요.';
    }

    if (!formData.requesterPhone.trim()) {
      newErrors.requesterPhone = '연락처를 입력해주세요.';
    } else {
      const phonePattern = /^01[0-9]-?\d{3,4}-?\d{4}$/;
      if (!phonePattern.test(formData.requesterPhone.replace(/-/g, ''))) {
        newErrors.requesterPhone = '올바른 전화번호 형식이 아닙니다.';
      }
    }

    if (formData.requesterEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.requesterEmail)) {
      newErrors.requesterEmail = '올바른 이메일 형식이 아닙니다.';
    }

    if (!formData.deceasedName.trim()) {
      newErrors.deceasedName = '고인명을 입력해주세요.';
    }

    if (!formData.funeralHome.trim()) {
      newErrors.funeralHome = '장례식장명을 입력해주세요.';
    }

    if (!agreePrivacy) {
      newErrors.agreePrivacy = '개인정보 수집 및 이용에 동의해주세요.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 접수번호 생성 (mock): REQ-YYYYMMDD-랜덤4자리
  const generateRequestNumber = () => {
    const ymd = todayISO().replace(/-/g, '');
    const rand = String(Math.floor(1000 + Math.random() * 9000));
    return `REQ-${ymd}-${rand}`;
  };

  // 제출
  const handleSubmit = (e) => {
    e.preventDefault();

    if (isSubmitting) return; // 중복 제출 방지

    if (!validate()) {
      showToast('필수 항목을 모두 입력하고 동의 항목을 확인해주세요.', 'error');
      return;
    }

    setIsSubmitting(true);

    // Mock 제출
    console.log('부고등록신청:', formData);

    setTimeout(() => {
      setRequestNumber(generateRequestNumber());
      setIsSubmitting(false);
      showToast('부고등록신청이 접수되었습니다.', 'success');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 800);
  };

  // 접수 완료 화면
  if (requestNumber) {
    return (
      <MainLayout>
        <div className="max-w-2xl mx-auto px-4 py-8">
          <Card className="text-center py-10">
            <CheckCircle className="mx-auto mb-4 text-green-500" size={56} />
            <h1 className="text-2xl font-bold text-primary-900 mb-2">신청이 접수되었습니다</h1>
            <p className="text-gray-600 mb-6">
              담당자가 확인 후 1시간 이내에 연락드리겠습니다.
            </p>
            <div className="mx-auto mb-6 inline-block rounded-lg bg-accent-50 border border-accent-200 px-6 py-4">
              <p className="text-sm text-gray-500 mb-1">접수번호</p>
              <p className="text-lg font-bold text-primary-900 tracking-wide">{requestNumber}</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href={`tel:${SUPPORT_PHONE}`} className="inline-flex">
                <Button variant="outline" className="flex items-center gap-2 w-full">
                  <Phone size={18} /> 고객센터 {SUPPORT_PHONE}
                </Button>
              </a>
              <Button variant="primary" onClick={() => navigate('/')}>
                홈으로
              </Button>
            </div>
          </Card>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-primary-900 mb-2">
            부고등록신청
          </h1>
          <p className="text-gray-600">
            부고 작성이 어려우신가요? 신청서를 작성해주시면 담당자가 도와드립니다.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <Card className="mb-6">
            <h2 className="text-lg font-semibold text-primary-900 mb-4 pb-3 border-b">
              신청자 정보
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  이름 <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  name="requesterName"
                  value={formData.requesterName}
                  onChange={handleChange}
                  placeholder="신청자 이름"
                  error={errors.requesterName}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  연락처 <span className="text-red-500">*</span>
                </label>
                <Input
                  type="tel"
                  name="requesterPhone"
                  value={formData.requesterPhone}
                  onChange={handleChange}
                  placeholder="010-1234-5678"
                  error={errors.requesterPhone}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  이메일 (선택)
                </label>
                <Input
                  type="email"
                  name="requesterEmail"
                  value={formData.requesterEmail}
                  onChange={handleChange}
                  placeholder="example@email.com"
                  error={errors.requesterEmail}
                />
              </div>
            </div>
          </Card>

          <Card className="mb-6">
            <h2 className="text-lg font-semibold text-primary-900 mb-4 pb-3 border-b">
              부고 기본 정보
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  고인명 <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  name="deceasedName"
                  value={formData.deceasedName}
                  onChange={handleChange}
                  placeholder="고인의 성함"
                  error={errors.deceasedName}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  장례식장명 <span className="text-red-500">*</span>
                </label>
                <Input
                  type="text"
                  name="funeralHome"
                  value={formData.funeralHome}
                  onChange={handleChange}
                  placeholder="예: 서울병원 장례식장"
                  error={errors.funeralHome}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  장례식장 주소 (선택)
                </label>
                <Input
                  type="text"
                  name="funeralAddress"
                  value={formData.funeralAddress}
                  onChange={handleChange}
                  placeholder="서울시 강남구..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  발인일 (선택)
                </label>
                <Input
                  type="date"
                  name="departureDate"
                  value={formData.departureDate}
                  onChange={handleChange}
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                  추가 요청사항 (선택)
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="부고 작성에 필요한 추가 정보나 요청사항을 입력해주세요."
                />
              </div>
            </div>
          </Card>

          {/* 개인정보 수집 동의 */}
          <Card className="mb-6">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={agreePrivacy}
                onChange={(e) => {
                  setAgreePrivacy(e.target.checked);
                  if (e.target.checked && errors.agreePrivacy) {
                    setErrors((prev) => {
                      const next = { ...prev };
                      delete next.agreePrivacy;
                      return next;
                    });
                  }
                }}
                className="mt-1 h-4 w-4 rounded border-accent-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">
                <span className="font-medium">[필수]</span> 부고 대행 작성을 위한 개인정보(신청자 성명·연락처·이메일) 수집 및 이용에 동의합니다.
              </span>
            </label>
            {errors.agreePrivacy && (
              <p className="mt-2 text-sm text-red-600">{errors.agreePrivacy}</p>
            )}
          </Card>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/')}
              className="flex-1"
              disabled={isSubmitting}
            >
              취소
            </Button>
            <Button
              type="submit"
              variant="primary"
              className="flex-1"
              disabled={isSubmitting}
            >
              {isSubmitting ? '접수 중...' : '신청하기'}
            </Button>
          </div>
        </form>

        {/* Info */}
        <div className="mt-8 p-4 bg-accent-50 rounded-lg border border-accent-200">
          <h3 className="font-semibold text-gray-900 mb-2">안내사항</h3>
          <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside mb-4">
            <li>신청서 접수 후 1시간 이내 담당자가 연락드립니다.</li>
            <li>상담 후 부고를 대신 작성해드립니다.</li>
            <li>긴급한 경우 고객센터로 직접 연락주세요.</li>
          </ul>
          <a href={`tel:${SUPPORT_PHONE}`} className="inline-flex">
            <Button variant="outline" className="flex items-center gap-2">
              <Phone size={18} /> 고객센터 {SUPPORT_PHONE}
            </Button>
          </a>
        </div>
      </div>
    </MainLayout>
  );
};

export default ObituaryRequestPage;
